import { type SlideWarningInput, getSlideWarning } from "@ai-teach/utils";

export type GrammarFormPublicationState =
  | "blocked_draft"
  | "ready_draft"
  | "hidden"
  | "published"
  | "not_applicable";

export interface PublicationStateSlide extends SlideWarningInput {
  isCompleted: boolean;
  isHidden: boolean;
}

/**
 * Publication state for a grammar-form slide (type==="grammar" && knowledgeArea==="form") — the only
 * slide kind with an explicit `isCompleted` publication bit. Pure and unit-testable: it reads
 * `isCompleted`/`isHidden` plus the shared `getSlideWarning` result (to distinguish a draft that's
 * blocked by content errors from one that's simply not yet published).
 *
 * | isCompleted | isHidden | state                            |
 * |-------------|----------|-----------------------------------|
 * | false       | either   | blocked_draft / ready_draft        |
 * | true        | true     | hidden                              |
 * | true        | false    | published                           |
 *
 * Any other slide kind returns "not_applicable".
 */
export function getGrammarFormPublicationState(
  slide: PublicationStateSlide
): GrammarFormPublicationState {
  if (slide.template !== "conversation") return "not_applicable";

  if (!slide.isCompleted) {
    const warnings = getSlideWarning(slide);
    const hasBlockingIssue = warnings.some((block) =>
      block.warningItem.some((item) => item.status === "incomplete" || item.status === "error")
    );
    return hasBlockingIssue ? "blocked_draft" : "ready_draft";
  }

  return slide.isHidden ? "hidden" : "published";
}
